import { isWeekend } from 'date-fns';
import { Frown, Info } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { ButtonLoadable } from '@/components/ui/button-loadable';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { Separator } from '@/components/ui/separator';
import { formatHoursToTimeString } from '@/helpers';
import { cn } from '@/lib/utils';
import { useApiError } from '@/modules/common/hooks/use-api-error';
import { formatLongWeekdayDate } from '@/utils/date-formatters';

import { useDayOverview } from '../../hooks/use-day-overview';
import { useViewModeAndDate } from '../../hooks/use-view-mode-and-date';
import { DayOverview, ViewMode } from '../../types/time-tracking';
import { UnifiedSummary, UnifiedSummaryVariant } from '../summary';
import { DayDetailPanelSkeleton } from './day-detail-panel-skeleton';
import { ProjectBreakdownRow } from './project-breakdown-row';

interface DayDetailPanelProps {
  selectedDay: Date | null;
}

export function DayDetailPanel({ selectedDay }: DayDetailPanelProps) {
  const { t } = useTranslation('authenticated.timeTracking');
  const { getErrorMessage } = useApiError();
  const { setViewModeAndDate } = useViewModeAndDate();

  const { data, isLoading, isRefetching, error, refetch } = useDayOverview({
    date: selectedDay,
  });

  if (!selectedDay) {
    return (
      <div className="w-full rounded-lg border bg-card p-6 text-center text-sm text-muted-foreground shadow-sm">
        {t('monthView.detail.selectADay')}
      </div>
    );
  }

  if (isLoading) return <DayDetailPanelSkeleton />;

  if (error) {
    return (
      <Empty className="w-full rounded-lg border bg-card">
        <EmptyMedia variant="icon">
          <Frown />
        </EmptyMedia>
        <EmptyTitle>{t('monthView.error.title')}</EmptyTitle>
        <EmptyDescription>{getErrorMessage(error)}</EmptyDescription>
        <EmptyContent>
          <ButtonLoadable
            variant="outline"
            size="sm"
            showRefreshIcon
            isLoading={isRefetching}
            onClick={() => refetch()}
          >
            {t('monthView.error.retry')}
          </ButtonLoadable>
        </EmptyContent>
      </Empty>
    );
  }

  if (!data) return null;

  const handleOpenInDayView = () => {
    setViewModeAndDate(ViewMode.Day, selectedDay);
  };

  return (
    <DayDetailContent day={data} selectedDay={selectedDay} onOpenInDayView={handleOpenInDayView} />
  );
}

function DayDetailContent({
  day,
  selectedDay,
  onOpenInDayView,
}: {
  day: DayOverview;
  selectedDay: Date;
  onOpenInDayView: () => void;
}) {
  const { t } = useTranslation('authenticated.timeTracking');
  const dayLabel = formatLongWeekdayDate(selectedDay);

  if (isWeekend(selectedDay) && day.capacityHours <= 0) {
    return (
      <div className="flex w-full flex-col gap-3 rounded-lg border bg-card p-5 shadow-sm">
        <div className="text-sm font-semibold">{dayLabel}</div>
        <p className="text-sm text-muted-foreground">{t('monthView.detail.weekendBody')}</p>
      </div>
    );
  }

  // Only surface projects that actually have activity on this day. A project
  // that's "assigned" but has neither booked nor tracked hours adds noise
  // without communicating anything — the empty-state copy on the right.
  const activeProjects = day.projectBreakdown.filter(p => p.bookedHours > 0 || p.trackedHours > 0);
  const totalTracked = activeProjects.reduce((sum, p) => sum + p.trackedHours, 0);
  const totalBooked = activeProjects.reduce((sum, p) => sum + p.bookedHours, 0);
  const hasProjects = activeProjects.length > 0;

  return (
    <div className="flex w-full flex-col gap-0 overflow-hidden rounded-lg border bg-card shadow-sm">
      {/* Day summary (compact, no outer card) */}
      <UnifiedSummary
        label={dayLabel}
        tracked={day.trackedHours}
        trackedBackground={day.trackedBreakdown
          .filter(b => b.isBackgroundWork)
          .reduce((sum, b) => sum + b.trackedHours, 0)}
        booked={day.bookedHours}
        effectiveCapacity={day.effectiveCapacityHours}
        timeOffApproved={day.timeOffApprovedHours}
        timeOffPending={day.timeOffPendingHours}
        variant={UnifiedSummaryVariant.Compact}
        bare
      />

      {hasProjects && (
        <>
          <Separator />
          <div className="flex flex-col gap-3 p-4">
            <SectionHeader>{t('monthView.detail.projectBreakdown')}</SectionHeader>
            <div className="flex flex-col gap-2">
              {activeProjects.map(p => {
                const isAllBackgroundWork =
                  p.bookedBreakdown.length > 0 && p.bookedBreakdown.every(b => b.isBackgroundWork);
                return (
                  <ProjectBreakdownRow
                    key={p.projectId}
                    item={{
                      projectId: p.projectId,
                      projectName: p.projectName,
                      colorHex: p.colorHex,
                      trackedHours: p.trackedHours,
                      bookedHours: p.bookedHours,
                      isBackgroundWork: isAllBackgroundWork,
                    }}
                    boxed
                  />
                );
              })}
            </div>
            <Separator />
            <div className="flex items-baseline justify-between text-[12px]">
              <span className="font-medium text-muted-foreground">
                {t('monthView.detail.totalsLabel')}
              </span>
              <span className="font-semibold tabular-nums">
                {t('monthView.detail.totalsValue', {
                  tracked: formatHoursToTimeString(totalTracked),
                  booked: formatHoursToTimeString(totalBooked),
                })}
              </span>
            </div>
          </div>
        </>
      )}

      <Separator />
      <div className={cn('flex flex-col gap-3 p-4')}>
        <EditHintAlert />
        <Button variant="default" onClick={onOpenInDayView} className="w-full">
          {t('monthView.detail.openInDayView')}
        </Button>
      </div>
    </div>
  );
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
      {children}
    </div>
  );
}

function EditHintAlert() {
  return (
    <div
      className={cn(
        'flex w-full items-start gap-2 rounded-md border bg-muted/50 px-3 py-2.5 text-[12.5px] text-muted-foreground'
      )}
    >
      <Info className="mt-0.5 h-4 w-4 shrink-0" />
      <span>
        <Trans
          i18nKey="authenticated.timeTracking:monthView.detail.editHint"
          components={{ strong: <strong className="font-semibold text-foreground" /> }}
        />
      </span>
    </div>
  );
}
